import _throttle from 'lodash/throttle';
import log from 'loglevel';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import VideoPlayer from 'Components/VideoPlayer';
import { ClassroomContext } from 'Context/ClassroomContext';
import { withCapacitor } from 'Util/Capacitors';
import { IS_OVERRIDE_LOADEDMETADATA } from 'Util/Capacitors/constant';
import getConfig from 'Util/Config';
import { isMobile } from 'Util/index';
import sanitizeVideoTime from 'Util/videoProgress';

import VideoBoxWrapper, { VIDEO_BOX_VARIANTS } from './VideoBoxWrapper';
import { MATERIAL_TYPE, VIDEO_PROVIDER } from './constant';

const config = getConfig();
const {
  videoPlayer: {
    resumeVideo,
    completedPercentage,
    analyticOptions,
    showVideoInfo,
    showVideoController,
    showVideoResolution,
    disableFullscreen,
    bigPlayButton,
    showUserOverlay,
    showVideoOverlayOnReady,
    showVideoOverlayOnPause,
    showVideoOverlayOnEnd,
    enableDoubleTapsVideoPlayer,
    enableVideoHotkeys,
  },
  classroomTimeInterval,
} = config;

const UPDATE_VIDEO_FACTOR = resumeVideo.updateFactor;
const RESUME_OFFSET_SEC = resumeVideo.resumeOffsetSec;

const THROTTLE_VIDEO_PROGRESS_MILLISEC = 1000;
const THROTTLE_UPDATE_VIDEO_TIME = 15 * UPDATE_VIDEO_FACTOR * 1000;

const THROTTLE_UPDATE_CLASSROOM_TIME_INTERVAL =
  classroomTimeInterval > 5000 ? classroomTimeInterval : 5000;

const VIDEO_COMPLETE_PERCENTAGE = completedPercentage;
class VideoBox extends Component {
  // TODO: separate VideoBox props and VideoPlayer props
  // or how to identify configs read from appConfig?
  static propTypes = {
    // for only VideoBox
    title: PropTypes.string,
    type: PropTypes.oneOf(Object.values(MATERIAL_TYPE)),
    value: PropTypes.shape({
      provider: PropTypes.oneOf(Object.values(VIDEO_PROVIDER)),
      videoId: PropTypes.string,
      url: PropTypes.string,
      thumbnailUrl: PropTypes.string,
      duration: PropTypes.number,
      channelName: PropTypes.string,
    }),
    secureVideo: PropTypes.func,
    secureLive: PropTypes.func,
    onUpdateVideoTime: PropTypes.func,

    isResuming: PropTypes.bool,
    videoProgress: PropTypes.shape({
      videoTime: PropTypes.number,
    }),
    checkInvalidDevice: PropTypes.func,
    onUpdateClassroomTime: PropTypes.func,
    isAppStateInactive: PropTypes.bool,
    onVideoError: PropTypes.func,

    userProfileData: PropTypes.object,

    // consumed by both VideoBox and VideoPlayer
    onCompleted: PropTypes.func,

    /**
     *  pass thru to VideoPlayer props
     *  See props explanation on VideoPlayer.js
     */
    delaySetCurrentTime: PropTypes.number,
    videoOrigin: PropTypes.string,

    isVideoJsControl: PropTypes.bool,
    isShowControl: PropTypes.bool,
    isShowInfo: PropTypes.bool,
    isShowResolution: PropTypes.bool,

    disableFullscreen: PropTypes.bool,
    bigPlayButton: PropTypes.bool,
    showUserOverlay: PropTypes.bool,
    showVideoOverlayOnReady: PropTypes.bool,
    showVideoOverlayOnPause: PropTypes.bool,
    showVideoOverlayOnEnd: PropTypes.bool,
    shouldHideControlOnSmallSize: PropTypes.bool,

    variant: PropTypes.oneOf(Object.values(VIDEO_BOX_VARIANTS)),

    logSubsectionVideoOnVideoPlayerError: PropTypes.func,
    currentLearningMode: PropTypes.string,
  };

  // FIXME: code below get videoPlayer from appConfig and fill as defaultProps???
  // should fill fallbacks at videoPlayer level?
  static defaultProps = {
    type: MATERIAL_TYPE.ARTICLE,
    secureVideo: () => {},
    secureLive: () => {},
    onUpdateVideoTime: () => {},
    onCompleted: () => {},
    videoProgress: {},
    checkInvalidDevice: () => {},
    onUpdateClassroomTime: () => {},
    onVideoError: () => {},
    isShowControl: showVideoController,
    isShowInfo: showVideoInfo,
    isShowResolution: showVideoResolution,

    disableFullscreen: disableFullscreen,
    bigPlayButton: bigPlayButton,
    showUserOverlay: showUserOverlay,
    showVideoOverlayOnReady: showVideoOverlayOnReady,
    showVideoOverlayOnPause: showVideoOverlayOnPause,
    showVideoOverlayOnEnd: showVideoOverlayOnEnd,
  };

  constructor(props) {
    super(props);
    this.state = {
      src: undefined,
      watched: false,
      isSetResumeTime: false,
    };
    this.watchedDuration = React.createRef();
    this.watchedDuration.current = 0;
    this.videoPlayer = React.createRef();
    this.hasLoggedDurationError = false;
  }

  logDurationError = (currentTime, videoDuration, source = 'general') => {
    if (this.hasLoggedDurationError) return;

    const { logSubsectionVideoOnVideoPlayerError, title, type, value } = this.props;

    if (typeof logSubsectionVideoOnVideoPlayerError === 'function') {
      logSubsectionVideoOnVideoPlayerError({
        videoProvider: value?.provider,
        subsectionTitle: title,
        subsectionContentType: type,
        errorMessage: `Duration Error (Source: ${source}): currentTime or videoDuration is zero/null`,
        videoWatchedDuration: currentTime,
        videoCompletionThreshold: 0,
        videoTotalDuration: videoDuration,
      });
      this.hasLoggedDurationError = true;
    }
  };

  getVideoProgressData = (source = 'general') => {
    const player = this.videoPlayer?.current;
    const currentTime = player?.getCurrentTime?.() || 0;
    const videoDuration = player?.getDuration?.() || 0;

    const isDurationValid = videoDuration > 0;
    const completedTime = isDurationValid ? (VIDEO_COMPLETE_PERCENTAGE * videoDuration) / 100 : 0;

    if (!isDurationValid) {
      this.logDurationError(currentTime, videoDuration, source);
    } else {
      this.hasLoggedDurationError = false;
    }

    return { currentTime, videoDuration, completedTime, isDurationValid };
  };

  getVideoSrc = async () => {
    this.hasLoggedDurationError = false;
    const {
      title,
      type,
      value: { provider, videoId, url, duration, channelName },
      secureVideo,
      secureLive,
      userProfileData,
    } = this.props;
    const { videoAccessToken, ...src } = await (async () => {
      switch (type) {
        case MATERIAL_TYPE.YOUTUBE: {
          return {
            src: url,
            type: 'video/youtube',
          };
        }
        case MATERIAL_TYPE.VIDEO: {
          if (provider !== VIDEO_PROVIDER.BYTEARK) {
            return {
              src: url,
              type: 'application/x-mpegURL',
            };
          }

          const { signedUrl, videoAccessToken } = await secureVideo(videoId, provider, duration);
          const { userId } = userProfileData;
          return {
            src: signedUrl,
            type: 'application/x-mpegURL',
            videoAccessToken,
            videoId,
            title,
            userId,
          };
        }
        case MATERIAL_TYPE.LIVE_VIDEO: {
          const { signedUrl } = await secureLive(channelName);
          const { userId } = userProfileData;
          return {
            src: signedUrl,
            type: 'application/x-mpegURL',
            videoId: channelName,
            title,
            userId,
          };
        }
        default:
          return {
            src: url,
            type: 'application/x-mpegURL',
          };
      }
    })();

    this.setState({ src, videoId, videoAccessToken, provider });
  };

  // Receive Event from VideoJS
  handleTimeUpdate = _throttle(() => {
    if (this.videoPlayer && this.videoPlayer.current) {
      const { currentTime, videoDuration, completedTime, isDurationValid } =
        this.getVideoProgressData('timeUpdate');

      const { watched, provider } = this.state;
      if (!watched && isDurationValid && currentTime >= completedTime) {
        log.debug('Marking video as completed now.');
        this.props.onCompleted(currentTime, completedTime, videoDuration, provider);
        this.setState({ watched: true });
      }
      this.props.checkInvalidDevice();
      if (this.props.type !== MATERIAL_TYPE.LIVE_VIDEO) {
        this.updateWatchedDuration();
        this.reportVideoTime(currentTime, videoDuration);
        this.updateClassroomTime();
      }
    }
  }, THROTTLE_VIDEO_PROGRESS_MILLISEC);

  /**
   * `state.videoId` is the media actually loaded in the player (set by getVideoSrc);
   * `props.value.videoId` is the asset the tree points at now. They diverge while
   * componentDidUpdate swaps the src in place — the old media keeps firing timeupdate
   * after props already describe the next asset.
   *
   * Known gap: for YouTube / plain-URL assets both sides are undefined, so this
   * degrades to always-true. Same blind spot as the componentDidUpdate guard below;
   * the duration clamp still protects those.
   */
  isPlayerMediaCurrent = () => this.state.videoId === this.props.value?.videoId;

  reportVideoTime = (currentTime, videoDuration) => {
    const videoTime = sanitizeVideoTime(currentTime, {
      playerDuration: videoDuration,
      assetDuration: this.props.value?.duration,
    });
    if (videoTime === null || !this.isPlayerMediaCurrent()) return;
    this.updateVideoTime({ videoTime, videoId: this.state.videoId });
  };

  // Throttle Callback to parent
  updateVideoTime = _throttle((sample) => {
    const { onUpdateVideoTime, value } = this.props;
    // Late flushes (up to THROTTLE_UPDATE_VIDEO_TIME) resolve subSectionReferId at call time, so a stale sample lands on the wrong subsection.
    if (!sample || sample.videoId !== value?.videoId) return;
    if (typeof onUpdateVideoTime === 'function') {
      onUpdateVideoTime(sample.videoTime);
    }
  }, THROTTLE_UPDATE_VIDEO_TIME);

  handleVideoPlay = () => {
    if (IS_OVERRIDE_LOADEDMETADATA && this.videoPlayer && this.videoPlayer.current) {
      const { current: player } = this.videoPlayer;

      const { isSetResumeTime } = this.state;
      if (!isSetResumeTime) {
        const resumeTime = this.getResumeTime();
        player.setCurrentTime(resumeTime, 0);
        this.setState({ isSetResumeTime: true });
      }
    }
  };

  updateWatchedDuration = () => {
    this.watchedDuration.current++;
  };

  updateClassroomTime = _throttle(() => {
    const { current: watchedDuration } = this.watchedDuration;
    const { onUpdateClassroomTime } = this.props;
    if (this.videoPlayer && this.videoPlayer.current && watchedDuration > 0) {
      if (typeof onUpdateClassroomTime === 'function') {
        onUpdateClassroomTime(watchedDuration);
      }
      this.watchedDuration.current = 0;
    }
  }, THROTTLE_UPDATE_CLASSROOM_TIME_INTERVAL);

  // If User Seek Video, send callback immediately
  handleSeek = () => {
    // Live streams have no stored duration and report a media-timeline position — same exclusion handleTimeUpdate makes.
    if (this.props.type === MATERIAL_TYPE.LIVE_VIDEO) return;
    if (this.videoPlayer && this.videoPlayer.current) {
      const { current: player } = this.videoPlayer;
      const videoTime = sanitizeVideoTime(player.getCurrentTime(), {
        playerDuration: player.getDuration(),
        assetDuration: this.props.value?.duration,
      });
      if (videoTime === null || !this.isPlayerMediaCurrent()) return;

      const { onUpdateVideoTime } = this.props;
      if (typeof onUpdateVideoTime === 'function') {
        onUpdateVideoTime(videoTime);
      }
    }
  };

  getResumeTime = () => {
    const { isResuming, videoProgress } = this.props;
    const { videoTime } = videoProgress || {};
    if (isResuming && videoTime > 0) {
      return Math.max(videoTime - RESUME_OFFSET_SEC, 0);
    }
    return undefined;
  };

  handleError = (error) => {
    const { onVideoError, logSubsectionVideoOnVideoPlayerError, title, type, value } = this.props;
    error.target.player.errorDisplay.close();

    const errorDetails = error.target && error.target.player ? error.target.player.error() : null;
    const { currentTime, videoDuration, completedTime } = this.getVideoProgressData('handleError');

    if (typeof logSubsectionVideoOnVideoPlayerError === 'function') {
      logSubsectionVideoOnVideoPlayerError({
        videoProvider: value.provider,
        subsectionTitle: title,
        subsectionContentType: type,
        errorMessage: errorDetails?.message || 'Unknown Error',
        videoWatchedDuration: currentTime,
        videoCompletionThreshold: completedTime,
        videoTotalDuration: videoDuration,
      });
    }

    if (typeof onVideoError === 'function') {
      onVideoError(errorDetails);
    }
  };

  shouldComponentUpdate = (newProps) => {
    if (newProps.isAppStateInactive !== this.props.isAppStateInactive) return false;
    return true;
  };

  componentDidMount = async () => {
    this.getVideoSrc();
    this.setState({
      watched: false,
    });
  };

  componentDidUpdate = async (prevProps, prevState) => {
    const {
      value: { videoId },
      currentLearningMode,
    } = this.props;

    const {
      value: { videoId: prevVideoId },
      currentLearningMode: prevCurrentLearningMode,
    } = prevProps;

    if (prevState.videoId !== videoId && prevVideoId !== videoId) {
      // Cancel (not flush) the pending sample: the subsection already moved on, so flushing would file the old video's time against the new subsection.
      this.handleTimeUpdate.cancel();
      this.updateVideoTime.cancel();
      this.getVideoSrc();
    } else if (prevCurrentLearningMode !== currentLearningMode) {
      this.getVideoSrc();
    }
  };

  componentWillUnmount() {
    if (this.videoPlayer && this.videoPlayer.current) {
      this.handleTimeUpdate.cancel();
      this.updateVideoTime.flush();
    }
  }

  render() {
    const {
      type,
      onCompleted,
      overlayDisplayName,
      overlayPhoneNumber,
      onOverlayCorrupted,
      delaySetCurrentTime,
      videoOrigin,
      capacitorContext,
      isVideoJsControl,
      isShowControl,
      isShowInfo,
      isShowResolution,
      disableFullscreen,
      bigPlayButton,
      showUserOverlay,
      showVideoOverlayOnReady,
      showVideoOverlayOnPause,
      showVideoOverlayOnEnd,
      renderCustomVideoOverlay,
      shouldHideControlOnSmallSize,
      variant,
      ...restProps
    } = this.props;

    const { isNative } = capacitorContext || {};
    const { src, videoAccessToken, provider } = this.state;

    const _resumeTime = IS_OVERRIDE_LOADEDMETADATA ? undefined : this.getResumeTime();

    // Prepare Autoplay behavior
    // force autoplay for Byteark by default
    let _isAutoplay = true;

    let _enableResolutionSwitcher = isShowResolution;

    // autoplay disabled only on mobile
    // https://www.notion.so/Video-Player-Documentation-d93b9f5c8c4b4aac8c2d6af9bc510a1c
    if (type === MATERIAL_TYPE.YOUTUBE) {
      if (isMobile) {
        _isAutoplay = false;
      }

      // disable because it's always shown "auto"
      _enableResolutionSwitcher = false;
    }

    return (
      <VideoBoxWrapper variant={variant}>
        {src && (
          <VideoPlayer
            ref={this.videoPlayer}
            src={this.state.src}
            videoAccessToken={this.state.videoAccessToken}
            videoProvider={this.state.provider}
            videoOrigin={videoOrigin}
            eventListeners={{
              // ended: onCompleted,
              timeupdate: this.handleTimeUpdate,
              play: this.handleVideoPlay,
              seeked: this.handleSeek,
              error: this.handleError,
            }}
            overlayDisplayName={overlayDisplayName}
            overlayPhoneNumber={overlayPhoneNumber}
            onOverlayCorrupted={onOverlayCorrupted}
            isOverrideLoadedmetadata={IS_OVERRIDE_LOADEDMETADATA}
            delaySetCurrentTime={delaySetCurrentTime}
            analyticOptions={analyticOptions}
            isVideoJsControl={isVideoJsControl}
            isNative={isNative}
            isShowControl={isShowControl}
            isShowInfo={isShowInfo}
            enableResolutionSwitcher={_enableResolutionSwitcher}
            disableFullscreen={disableFullscreen}
            bigPlayButton={bigPlayButton}
            showUserOverlay={showUserOverlay}
            showVideoOverlayOnReady={showVideoOverlayOnReady}
            showVideoOverlayOnPause={showVideoOverlayOnPause}
            showVideoOverlayOnEnd={showVideoOverlayOnEnd}
            renderCustomVideoOverlay={renderCustomVideoOverlay}
            shouldHideControlOnSmallSize={shouldHideControlOnSmallSize}
            enableDoubleTapsVideoPlayer={enableDoubleTapsVideoPlayer}
            enableHotkeys={enableVideoHotkeys}
            // not VideoBox props
            resumeTime={_resumeTime}
            isAutoplay={_isAutoplay}
          />
        )}
      </VideoBoxWrapper>
    );
  }
}

const VideoBoxWithContext = (props) => (
  <ClassroomContext.Consumer>
    {({ currentLearningMode }) => <VideoBox {...props} currentLearningMode={currentLearningMode} />}
  </ClassroomContext.Consumer>
);

export default withCapacitor(VideoBoxWithContext);
